import { Fragment, type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

export interface PositionAdjustmentResult {
	adjusted: boolean;
	startPos: number;
	userContentEndPos: number;
}

/**
 * Pure version of adjustStartEndPosForEmptySelection
 */
export function calculateEmptySelectionAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const { paragraph, listItem, taskItem, decisionItem } = state.schema.nodes;
	const bailout = { startPos, userContentEndPos, adjusted: false };

	// Validate positions
	if (startPos < 0 || startPos > state.doc.content.size) {
		return bailout;
	}

	const $start = state.doc.resolve(startPos);
	const startParent = $start.parent;

	const inList =
		startParent.type === paragraph &&
		$start.depth > 1 &&
		$start.node($start.depth - 1)?.type === listItem;
	const inTaskItem = taskItem && startParent.type === taskItem;
	const inDecisionItem = decisionItem && startParent.type === decisionItem;

	if (!inList && !inTaskItem && !inDecisionItem) {
		return bailout;
	}

	const $end = state.doc.resolve(userContentEndPos);
	const endParent = $end.parent;

	const newNode = fragment.firstChild;
	const oneBlock = fragment.childCount === 1 && newNode && newNode.isBlock;
	const newStartBlock = oneBlock && newNode.type.name !== startParent.type.name;
	const newEndBlock = oneBlock && newNode.type.name !== endParent.type.name;

	let startPosAdjusted = startPos;
	let endPosAdjusted = userContentEndPos;

	if (newStartBlock) {
		for (let depth = $start.depth, diff = 1; depth > 0; depth--, diff++, startPosAdjusted--) {
			const ancestor = $start.node(depth);
			if (ancestor.type.contentMatch.matchType(newNode.type)) {
				break;
			}
			const before = $start.before(depth);
			if (before + diff !== startPos) {
				break;
			}
		}
	}

	if (newEndBlock) {
		for (let depth = $end.depth, diff = 1; depth > 0; depth--, diff++, endPosAdjusted++) {
			const ancestor = $end.node(depth);
			if (ancestor.type.contentMatch.matchType(newNode.type)) {
				break;
			}
			const after = $end.after(depth);
			if (after - diff !== userContentEndPos) {
				break;
			}
		}
	}

	if (startPosAdjusted !== bailout.startPos || endPosAdjusted !== bailout.userContentEndPos) {
		const pos =
			endPosAdjusted - userContentEndPos >= startPos - startPosAdjusted
				? endPosAdjusted
				: startPosAdjusted;

		return { startPos: pos, userContentEndPos: pos, adjusted: true };
	}

	return bailout;
}

/**
 * Pure version of adjustStartEndPosForRangeSelection
 */
export function calculateRangeSelectionAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const { paragraph } = state.schema.nodes;
	const bailout = { startPos, userContentEndPos, adjusted: false };

	// Validate positions
	if (startPos < 0 || startPos > state.doc.content.size) {
		return bailout;
	}

	let $start = state.doc.resolve(startPos);

	if ($start.depth > 0 && $start.before() !== startPos - 1) {
		return bailout;
	}

	const types: NodeType[] = [];
	let node = fragment.firstChild;
	while (node && node.firstChild) {
		types.push(node.type);
		node = node.firstChild;
	}
	types.reverse();

	if (types.length === 1 && types[0] === paragraph) {
		return bailout;
	}

	let startPosAdjusted = startPos;
	node = $start.parent.firstChild;
	while (node && node.firstChild) {
		startPosAdjusted++;
		node = node.firstChild;
	}

	$start = state.doc.resolve(startPosAdjusted);
	for (let i = 0, depth = $start.depth; depth > 0; i++, depth--, startPosAdjusted--) {
		const ancestor = $start.node(depth);
		if (ancestor.type !== types[i]) {
			break;
		}
		const before = $start.before(depth);
		if (before + 1 !== startPosAdjusted) {
			break;
		}
	}

	if (startPosAdjusted < bailout.startPos) {
		return {
			startPos: startPosAdjusted,
			userContentEndPos: bailout.userContentEndPos,
			adjusted: true,
		};
	}

	return bailout;
}

/**
 * Pure version of adjustStartAndUserContentEndPosForList
 */
export function calculateListAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const { listItem, paragraph } = state.schema.nodes;
	const bailout = { startPos, userContentEndPos, adjusted: false };

	if (!listItem) {
		return bailout;
	}

	const doc = state.doc;
	const docSize = doc.content.size;

	// Validate positions
	if (startPos < 0 || startPos > docSize || userContentEndPos < 0 || userContentEndPos > docSize) {
		return bailout;
	}

	let startPosAdjusted = startPos;
	let userContentEndPosAdjusted = userContentEndPos;

	const fragmentFirstNode = fragment.firstChild;
	if (!fragmentFirstNode) {
		return bailout;
	}
	/**
	 * If first child can be child of list item then no need to adjust positions.
	 * In case of table, layout or panel, first child won't be valid content of list item,
	 * 	thus adjustments will happen to avoid dangling list items.
	 */
	const firstChildFragment = Fragment.from(fragmentFirstNode);
	const isFirstValidListItemContent = listItem?.validContent(firstChildFragment);
	let shouldAdjustPositions = false;
	if (expValEquals('platform_editor_ai_aifc_listitem_indentation_fix', 'isEnabled', true)) {
		shouldAdjustPositions = !isFirstValidListItemContent;
	} else {
		shouldAdjustPositions = fragmentFirstNode.isBlock;
	}
	if (!shouldAdjustPositions) {
		return bailout;
	}

	const $start = doc.resolve(startPos);
	let curParent = $start.node($start.depth);
	let curParentParent = $start.depth > 1 ? $start.node($start.depth - 1) : null;

	if (
		curParent.type === paragraph &&
		$start.parentOffset === 0 &&
		curParentParent?.type === listItem
	) {
		startPosAdjusted -= 2;
	} else if (curParent.type === listItem) {
		startPosAdjusted -= 1;
	}

	const $userContentEnd = doc.resolve(userContentEndPos);
	curParent = $userContentEnd.node($userContentEnd.depth);
	curParentParent =
		$userContentEnd.depth > 1 ? $userContentEnd.node($userContentEnd.depth - 1) : null;

	if (
		curParent.type === paragraph &&
		$userContentEnd.parentOffset === curParent.content.size &&
		curParentParent?.type === listItem
	) {
		userContentEndPosAdjusted += 2;
	} else if (curParent.type === listItem) {
		userContentEndPosAdjusted += 1;
	}

	// Clamp to valid range
	startPosAdjusted = Math.max(0, startPosAdjusted);
	userContentEndPosAdjusted = Math.min(docSize, userContentEndPosAdjusted);

	const adjusted = startPosAdjusted !== startPos || userContentEndPosAdjusted !== userContentEndPos;

	return { startPos: startPosAdjusted, userContentEndPos: userContentEndPosAdjusted, adjusted };
}
